import { useEffect, useState } from "react";
import { Box, Typography, CircularProgress } from "@mui/material";
import { motion, AnimatePresence } from "framer-motion";
import { CheckCircle, ErrorOutline } from "@mui/icons-material";
import { colors, alphaVar } from "../../../../../ui/colors";

export const QuoteProcessingStatus = ({
  isProcessing,
  status,
  message,
  autoHide = true,
  duration = 4000,
  onHide,
}) => {
  const [visible, setVisible] = useState(true);

  useEffect(() => {
    if ((status === "success" || status === "error") && autoHide) {
      const timer = setTimeout(() => {
        setVisible(false);
        if (onHide) onHide();
      }, duration);
      return () => clearTimeout(timer);
    }
  }, [status, autoHide, duration, onHide]);

  const variants = {
    initial: { opacity: 0, scale: 0.95 },
    animate: { opacity: 1, scale: 1, transition: { duration: 0.4 } },
    exit: { opacity: 0, scale: 0.95, transition: { duration: 0.6, ease: "easeInOut" } },
  };

  if (isProcessing) {
    return (
      <Box
        sx={{
          position: "fixed",
          inset: 0,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          bgcolor: "rgba(var(--fx-surface-basergb, 255, 255, 255), 0.8)",
          zIndex: 9999,
          backdropFilter: "blur(2px)",
        }}
      >
        <CircularProgress size={60} sx={{ color: colors.orange.base, mb: 2 }} />
        <Typography variant="h6" sx={{ color: colors.gray[800], fontWeight: 600 }}>
          Processing your request...
        </Typography>
        {message && (
          <Typography variant="body2" sx={{ color: colors.gray[600], mt: 1 }}>
            {message}
          </Typography>
        )}
      </Box>
    );
  }

  return (
    <AnimatePresence>
      {visible && (status === "success" || status === "error") && (
        <motion.div
          key={status}
          variants={variants}
          initial="initial"
          animate="animate"
          exit="exit"
          style={{
            position: "fixed",
            inset: 0,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 9999,
            backgroundColor:
              status === "success"
                ? alphaVar(colors.success[50], 0.9)
                : alphaVar(colors.danger[50], 0.9),
          }}
        >
          {status === "success" ? (
            <>
              <CheckCircle sx={{ fontSize: 80, color: colors.success[600], mb: 1 }} />
              <Typography variant="h5" sx={{ fontWeight: 700, color: colors.success[700] }}>
                Quote Accepted
              </Typography>
              {message && (
                <Typography variant="body2" sx={{ color: colors.success[600], mt: 1 }}>
                  {message}
                </Typography>
              )}
            </>
          ) : (
            <>
              <ErrorOutline sx={{ fontSize: 80, color: colors.danger[600], mb: 1 }} />
              <Typography variant="h5" sx={{ fontWeight: 700, color: colors.danger[700] }}>
                Something went wrong
              </Typography>
              {message && (
                <Typography variant="body2" sx={{ color: colors.danger[600], mt: 1 }}>
                  {message}
                </Typography>
              )}
            </>
          )}
        </motion.div>
      )}
    </AnimatePresence>
  );
};
